import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import useSiteBootstrap from '../hooks/useSiteBootstrap';

function normalizeMeta(metadata) {
  if (!metadata) return {};
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch {
      return {};
    }
  }
  return metadata;
}

function formatLabel(value) {
  return String(value || '')
    .replace('feature_', '')
    .replaceAll('_', ' ')
    .trim();
}

function getPlanFeatures(meta) {
  if (Array.isArray(meta?.features_list)) {
    return meta.features_list.filter(Boolean).map(formatLabel);
  }

  if (meta?.features && typeof meta.features === 'object') {
    return Object.entries(meta.features)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([key]) => formatLabel(key));
  }

  return [];
}

function getPlanLimits(meta) {
  if (!meta?.limits || typeof meta.limits !== 'object') return [];

  return Object.entries(meta.limits)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${formatLabel(key)}: ${value}`);
}

export default function PricingPage() {
  const site = useSiteBootstrap();
  const [billing, setBilling] = useState('monthly');

  const plans = Array.isArray(site?.plans) ? site.plans : [];
  const settings = site?.settings || {};

  const pageTitle = useMemo(() => {
    const base = String(settings?.seo_title || settings?.site_name || '').trim();
    if (!base) return '';
    return `Pricing | ${base}`;
  }, [settings?.seo_title, settings?.site_name]);

  const pageDescription =
    settings?.seo_description || '';

  const pageKeywords =
    settings?.seo_keywords || '';

  return (
    <div className="landing-scope">
      <Helmet>
        {pageTitle ? <title>{pageTitle}</title> : null}
        {pageDescription ? <meta name="description" content={pageDescription} /> : null}
        {pageKeywords ? <meta name="keywords" content={pageKeywords} /> : null}
        <link rel="icon" href={settings?.favicon_url || '/favicon.ico'} />
      </Helmet>

      <MainNavbar />

      <section className="landing-section pricing-section-v2">
        <Container>
          <div className="landing-section-heading text-center">
            <span className="landing-section-heading__eyebrow">Pricing</span>
            <h1 className="landing-section-heading__title">Simple, scalable pricing</h1>
            <p className="landing-section-heading__subtitle">
              Choose the plan that fits your team size, support volume, and growth stage.
            </p>
          </div>

          <div className="d-flex justify-content-center mb-5">
            <ToggleButtonGroup
              type="radio"
              name="billing"
              value={billing}
              onChange={(value) => setBilling(value)}
            >
              <ToggleButton
                id="billing-monthly"
                value="monthly"
                variant={billing === 'monthly' ? 'primary' : 'outline-primary'}
              >
                Monthly
              </ToggleButton>

              <ToggleButton
                id="billing-annual"
                value="annual"
                variant={billing === 'annual' ? 'primary' : 'outline-primary'}
              >
                Yearly
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          <Row className="g-4 justify-content-center">
            {plans.map((plan) => {
              const meta = normalizeMeta(plan?.metadata);
              const isPopular = Boolean(
                meta?.popular_plan || meta?.most_popular || meta?.popular
              );
              const features = getPlanFeatures(meta);
              const limits = getPlanLimits(meta);
              const price =
                Number(
                  billing === 'annual'
                    ? plan?.annual_price_usd
                    : plan?.monthly_price_usd
                ) || 0;

              return (
                <Col xl={4} lg={6} md={6} key={plan.id} className="d-flex">
                  <Card
                    className={`pricing-card-v2 w-100 border-0 ${
                      isPopular ? 'pricing-card-v2--popular' : ''
                    }`}
                  >
                    <Card.Body className="pricing-card-v2__body">
                      {isPopular ? (
                        <div className="pricing-card-v2__badge">Most Popular</div>
                      ) : null}

                      {plan?.name ? (
                        <h3 className="pricing-card-v2__title">{plan.name}</h3>
                      ) : null}

                      <div className="pricing-card-v2__price-wrap">
                        <div className="pricing-card-v2__price">
                          <span className="pricing-card-v2__currency">$</span>
                          <span className="pricing-card-v2__amount">
                            {price.toLocaleString()}
                          </span>
                        </div>

                        <span className="pricing-card-v2__suffix">
                          /{billing === 'annual' ? 'year' : 'month'}
                        </span>
                      </div>

                      {billing === 'annual' && Number(plan?.monthly_price_usd || 0) > 0 ? (
                        <p className="pricing-card-v2__annual">
                          Billed annually for better value
                        </p>
                      ) : (
                        <p className="pricing-card-v2__annual">
                          Flexible {billing === 'annual' ? 'yearly' : 'monthly'} billing
                        </p>
                      )}

                      <div className="small fw-semibold mb-2 text-dark">Included features</div>
                      <ul className="pricing-card-v2__features">
                        {(features.length ? features : ['Core platform access']).map(
                          (feature, index) => (
                            <li
                              className="text-capitalize"
                              key={`${plan?.id || 'plan'}-feature-${index}`}
                            >
                              {feature}
                            </li>
                          )
                        )}
                      </ul>

                      <div className="small fw-semibold mb-2 text-dark">Plan limits</div>
                      <ul className="pricing-card-v2__features mb-4">
                        {(limits.length ? limits : ['Standard limits']).map(
                          (limit, index) => (
                            <li
                              className="text-capitalize"
                              key={`${plan?.id || 'plan'}-limit-${index}`}
                            >
                              {limit}
                            </li>
                          )
                        )}
                      </ul>

                      <Button as={Link} to="/signup" className="pricing-card-v2__button">
                        Get started
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}

            {!plans.length ? (
              <Col lg={8}>
                <Card className="pricing-card-v2 w-100 border-0">
                  <Card.Body className="pricing-card-v2__body text-center">
                    <h3 className="pricing-card-v2__title mb-2">No active plans yet</h3>
                    <p className="pricing-card-v2__annual mb-0">
                      No active plans are available right now.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            ) : null}
          </Row>
        </Container>
      </section>

      <MainFooter />
    </div>
  );
}
